import styles from "./NoticeOne.module.css";
import { useState, useEffect } from "react";
import { toCamelCase } from "../../Hooks/utility";
import { usePreviewStores } from "../../data/CmsPreviewContext";
import useDetermineAndCleanJSXOutput from "../../Hooks/useDetermineAndCleanJSXOutput";
import useShortCodes from "../../Hooks/useShortCodes";
import useItemIsUsed from "../../Hooks/useItemIsUsed";
import SectionHeroTitle from "../SectionHeroTitle/SectionHeroTitle";
import { pickContentByType } from "../../data/contentPicker";

/** True only when a URL string has real content (not "", " ", etc.). */
const hasMediaUrl = (value) =>
  typeof value === "string" && value.replace(/\s/g, "").length > 0;

const NoticeOne = () => {
  const { content } = usePreviewStores();
  const [noticeOneData, setNoticeOneData] = useState({});
  const shortCodes = useShortCodes();
  const itemIsUsed = useItemIsUsed();
  // Skip empty Quill shells (<p><br></p>) but keep space-only / real copy.
  const textOne = itemIsUsed(noticeOneData.text)
    ? shortCodes({ text: noticeOneData.text, styles })
    : null;
  const textTwo = itemIsUsed(noticeOneData.textTwo)
    ? shortCodes({ text: noticeOneData.textTwo, styles })
    : null;
  const textThree = itemIsUsed(noticeOneData.textThree)
    ? shortCodes({ text: noticeOneData.textThree, styles })
    : null;
  const textFour = itemIsUsed(noticeOneData.textFour)
    ? shortCodes({ text: noticeOneData.textFour, styles })
    : null;
  const hasImageTwo = hasMediaUrl(noticeOneData.imageTwoURL);
  const hasImageThree = hasMediaUrl(noticeOneData.imageThreeURL);
  const hasImageFour = hasMediaUrl(noticeOneData.imageFourURL);
  const determineAndCleanJSXOutput = useDetermineAndCleanJSXOutput();

  useEffect(() => {
    setNoticeOneData(pickContentByType(content, "noticeOne") || {});
  }, [content]);

  // Assemble user-supplied image css
  const imageCSSNamesArray = [
    "imageOneCSS",
    "imageTwoCSS",
    "imageThreeCSS",
    "imageFourCSS",
  ];
  const imageCSS = [];

  if (noticeOneData) {
    for (const imageCSSName of imageCSSNamesArray) {
      if (Object.hasOwn(noticeOneData, imageCSSName)) {
        imageCSS[imageCSSName] = {};
        const imageCSSNameValueArray = noticeOneData[imageCSSName]
          .replaceAll('"', "")
          .replaceAll("\n", "")
          .split("|");

        imageCSSNameValueArray.forEach((nameAndValue) => {
          if (!nameAndValue) return;
          const name = toCamelCase(
            nameAndValue.split(":")[0].replaceAll(" ", ""),
          );
          let value = nameAndValue.split(":")[1].trim();
          imageCSS[imageCSSName][name] = value;
        });
      }
    }
  }
  ///////

  return (
    <div className={styles["noticeOne-container"]}>
      <a
        name="noticeOne"
        href="#noticeOne"
        className={styles["anchor-page-bookmark"]}
      >
        <span></span>
      </a>
      <div className={styles["noticeOne-text-container"]}>
        {/* Step 1: shared hero title with hardcoded preview image */}
        <div className={styles["section-hero-wrap"]}>
          <SectionHeroTitle
            title={noticeOneData.title || "Notice"}
            headingLevel={3}
            imageOpacity={0.125}
            imageOpacityMobile={0.5}
            imageOpacityUnderside={0.75} // lower plane (defaults to imageOpacity if omitted)
            imageOpacityUndersideMobile={0.33}
            showImageUnderside
          />
        </div>
        {textOne && (
          <p
            className={
              styles["text"] +
              " " +
              styles["text-one"] +
              " " +
              styles["content"]
            }
          >
            {determineAndCleanJSXOutput(textOne)}
          </p>
        )}{" "}
        {hasMediaUrl(noticeOneData.link) && (
          <div className={styles["link-wrap"]}>
            <a target="_blank" rel="noreferrer" href={noticeOneData.link}>
              {noticeOneData.link}
            </a>
          </div>
        )}
        {(hasImageTwo || textTwo) && (
          <div
            className={
              styles["text-pic-container"] +
              " " +
              styles["text-pic-container-two"]
            }
          >
            {" "}
            {hasImageTwo && (
              <div className={styles["image-wrap"]}>
                <figure>
                  <img
                    src={noticeOneData.imageTwoURL}
                    className={
                      styles["image"] +
                      " " +
                      styles["image-two"] +
                      " " +
                      styles["content"]
                    }
                    alt={noticeOneData.title + " second image "}
                    style={{ ...imageCSS.imageTwoCSS }}
                  />
                </figure>
              </div>
            )}
            {textTwo && (
              <p
                className={
                  styles["text"] +
                  " " +
                  styles["text-two"] +
                  " " +
                  styles["content"]
                }
              >
                {determineAndCleanJSXOutput(textTwo)}
              </p>
            )}{" "}
          </div>
        )}
        {(hasImageThree || textThree) && (
          <div
            className={
              styles["text-pic-container"] +
              " " +
              styles["text-pic-container-three"]
            }
          >
            {textThree && (
              <p
                className={
                  styles["text"] +
                  " " +
                  styles["text-three"] +
                  " " +
                  styles["content"]
                }
              >
                {determineAndCleanJSXOutput(textThree)}
              </p>
            )}
            {hasImageThree && (
              <div className={styles["image-wrap"]}>
                <figure>
                  <img
                    src={noticeOneData.imageThreeURL}
                    className={
                      styles["image"] +
                      " " +
                      styles["image-three"] +
                      " " +
                      styles["content"]
                    }
                    alt={noticeOneData.title + " third image "}
                    style={{ ...imageCSS.imageThreeCSS }}
                  />
                </figure>
              </div>
            )}
          </div>
        )}
        {(hasImageFour || textFour) && (
          <div
            className={
              styles["text-pic-container"] +
              " " +
              styles["text-pic-container-four"]
            }
          >
            {" "}
            {textFour && (
              <p
                className={
                  styles["text"] +
                  " " +
                  styles["text-four"] +
                  " " +
                  styles["content"]
                }
              >
                {determineAndCleanJSXOutput(textFour)}
              </p>
            )}
            {hasImageFour && (
              <div className={styles["image-wrap"]}>
                {" "}
                <figure>
                  <img
                    src={noticeOneData.imageFourURL}
                    className={
                      styles["image"] +
                      " " +
                      styles["image-four"] +
                      " " +
                      styles["content"]
                    }
                    alt={noticeOneData.title + " fourth image "}
                    style={{ ...imageCSS.imageFourCSS }}
                  />
                </figure>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default NoticeOne;
